// AvatarCropModal
// ----------------
// Lets the user position and scale an image before it becomes their avatar.
//
// Why crop instead of storing an offset:
//   Avatars are rendered with `object-cover` in a dozen places (menu bar,
//   account page, user lists). Storing a per-user focal point would mean
//   teaching every one of those to honour it, plus a schema and resolver
//   change. Baking the crop into the uploaded image keeps the change entirely
//   front-end: the existing presigned PUT uploads the cropped result instead of
//   the original, and everything downstream is unchanged.
//
// Output:
//   A square PNG at OUTPUT_SIZE_PIXELS. PNG because it is lossless, is already
//   on the avatar MIME allowlist, and at this size the file stays small. Note
//   that an animated GIF is flattened to its first frame by this path — that is
//   the trade for being able to reposition it.
//
// The result is re-validated by the caller through validateAvatarFile, so the
// cropped output goes through exactly the same size/MIME/magic-byte checks as a
// directly chosen file. Nothing here is a security control: the backend remains
// the authoritative validator.

import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Loader2, X, ZoomIn } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useModalAccessibility } from "@/utils/useModalAccessibility";

// Rendered avatar is 96px at its largest today; 512 leaves headroom for
// retina and for larger placements later without producing a heavy file.
const OUTPUT_SIZE_PIXELS = 512;
const OUTPUT_MIME_TYPE = "image/png";
const OUTPUT_FILENAME = "avatar.png";

// On-screen editing viewport. Square, because the avatar is always a circle
// cropped from a square.
const VIEWPORT_SIZE_PIXELS = 256;

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.01;

interface AvatarCropModalProps {
  open: boolean;
  imageFile: File | null;
  onCancel: () => void;
  onCropped: (croppedFile: File) => void;
}

interface PanOffset {
  x: number;
  y: number;
}

export default function AvatarCropModal({
  open,
  imageFile,
  onCancel,
  onCropped,
}: AvatarCropModalProps) {
  const dialogContainerRef = useRef<HTMLDivElement>(null);
  const imageElementRef = useRef<HTMLImageElement | null>(null);
  const dragStartRef = useRef<{ pointerX: number; pointerY: number; offset: PanOffset } | null>(null);

  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [panOffset, setPanOffset] = useState<PanOffset>({ x: 0, y: 0 });
  const [isExporting, setIsExporting] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useModalAccessibility(open, onCancel, dialogContainerRef);

  // Hold an object URL for the chosen file, and revoke it when we are done so
  // repeated picks do not leak blobs.
  useEffect(() => {
    if (!open || !imageFile) {
      setObjectUrl(null);
      return;
    }
    const createdUrl = URL.createObjectURL(imageFile);
    setObjectUrl(createdUrl);
    setZoom(MIN_ZOOM);
    setPanOffset({ x: 0, y: 0 });
    setNaturalSize(null);
    setLoadFailed(false);
    setIsExporting(false);
    return () => URL.revokeObjectURL(createdUrl);
  }, [open, imageFile]);

  // The image is scaled so its SHORTER side fills the viewport at zoom 1, which
  // is what object-cover does. Everything else is derived from this.
  const baseScale = naturalSize
    ? Math.max(
        VIEWPORT_SIZE_PIXELS / naturalSize.width,
        VIEWPORT_SIZE_PIXELS / naturalSize.height
      )
    : 1;
  const renderedWidth = naturalSize ? naturalSize.width * baseScale * zoom : 0;
  const renderedHeight = naturalSize ? naturalSize.height * baseScale * zoom : 0;

  // Pan is clamped so the image can never be dragged away from the viewport
  // edges, which would otherwise leave transparent gaps in the exported avatar.
  const clampOffset = useCallback(
    (candidate: PanOffset): PanOffset => {
      const maximumX = Math.max(0, (renderedWidth - VIEWPORT_SIZE_PIXELS) / 2);
      const maximumY = Math.max(0, (renderedHeight - VIEWPORT_SIZE_PIXELS) / 2);
      return {
        x: Math.min(maximumX, Math.max(-maximumX, candidate.x)),
        y: Math.min(maximumY, Math.max(-maximumY, candidate.y)),
      };
    },
    [renderedWidth, renderedHeight]
  );

  // Re-clamp whenever the zoom changes, or zooming out could leave the image
  // parked outside its own bounds.
  useEffect(() => {
    setPanOffset((currentOffset) => clampOffset(currentOffset));
  }, [clampOffset]);

  const handleImageLoad = (loadEvent: React.SyntheticEvent<HTMLImageElement>) => {
    const loadedImage = loadEvent.currentTarget;
    imageElementRef.current = loadedImage;
    setNaturalSize({ width: loadedImage.naturalWidth, height: loadedImage.naturalHeight });
  };

  const handlePointerDown = (pointerEvent: React.PointerEvent<HTMLDivElement>) => {
    pointerEvent.currentTarget.setPointerCapture(pointerEvent.pointerId);
    dragStartRef.current = {
      pointerX: pointerEvent.clientX,
      pointerY: pointerEvent.clientY,
      offset: panOffset,
    };
  };

  const handlePointerMove = (pointerEvent: React.PointerEvent<HTMLDivElement>) => {
    const dragStart = dragStartRef.current;
    if (!dragStart) return;
    setPanOffset(
      clampOffset({
        x: dragStart.offset.x + (pointerEvent.clientX - dragStart.pointerX),
        y: dragStart.offset.y + (pointerEvent.clientY - dragStart.pointerY),
      })
    );
  };

  const handlePointerUp = (pointerEvent: React.PointerEvent<HTMLDivElement>) => {
    if (pointerEvent.currentTarget.hasPointerCapture(pointerEvent.pointerId)) {
      pointerEvent.currentTarget.releasePointerCapture(pointerEvent.pointerId);
    }
    dragStartRef.current = null;
  };

  // Keyboard nudge, so repositioning is not pointer-only.
  const handleViewportKeyDown = (keyboardEvent: React.KeyboardEvent<HTMLDivElement>) => {
    const nudgePixels = keyboardEvent.shiftKey ? 20 : 5;
    const nudgeByKey: Record<string, PanOffset> = {
      ArrowLeft: { x: -nudgePixels, y: 0 },
      ArrowRight: { x: nudgePixels, y: 0 },
      ArrowUp: { x: 0, y: -nudgePixels },
      ArrowDown: { x: 0, y: nudgePixels },
    };
    const nudge = nudgeByKey[keyboardEvent.key];
    if (!nudge) return;
    keyboardEvent.preventDefault();
    setPanOffset((currentOffset) =>
      clampOffset({ x: currentOffset.x + nudge.x, y: currentOffset.y + nudge.y })
    );
  };

  const handleApply = async () => {
    const loadedImage = imageElementRef.current;
    if (!loadedImage || !naturalSize || isExporting) return;

    setIsExporting(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = OUTPUT_SIZE_PIXELS;
      canvas.height = OUTPUT_SIZE_PIXELS;
      const drawingContext = canvas.getContext("2d");
      if (!drawingContext) {
        setLoadFailed(true);
        return;
      }

      // Map the on-screen transform onto the output canvas. The viewport is
      // VIEWPORT_SIZE_PIXELS wide and the canvas is OUTPUT_SIZE_PIXELS, so
      // every on-screen dimension scales by this ratio.
      const viewportToCanvas = OUTPUT_SIZE_PIXELS / VIEWPORT_SIZE_PIXELS;
      const drawWidth = renderedWidth * viewportToCanvas;
      const drawHeight = renderedHeight * viewportToCanvas;
      const drawX = (OUTPUT_SIZE_PIXELS - drawWidth) / 2 + panOffset.x * viewportToCanvas;
      const drawY = (OUTPUT_SIZE_PIXELS - drawHeight) / 2 + panOffset.y * viewportToCanvas;

      drawingContext.imageSmoothingQuality = "high";
      drawingContext.drawImage(loadedImage, drawX, drawY, drawWidth, drawHeight);

      const croppedBlob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, OUTPUT_MIME_TYPE)
      );
      if (!croppedBlob) {
        setLoadFailed(true);
        return;
      }

      onCropped(
        new File([croppedBlob], OUTPUT_FILENAME, { type: OUTPUT_MIME_TYPE })
      );
    } catch (cropError) {
      // A tainted canvas or an out-of-memory export. Nothing here is worth
      // surfacing verbatim.
      console.error("Avatar crop failed");
      setLoadFailed(true);
    } finally {
      setIsExporting(false);
    }
  };

  if (!open || !imageFile) return null;

  const isReady = !!naturalSize && !loadFailed;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} aria-hidden="true" />

      <div
        ref={dialogContainerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="avatar-crop-title"
        className="relative flex w-full max-w-sm flex-col max-h-[90vh] rounded-xl bg-white shadow-xl"
      >
        <div className="flex shrink-0 items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 id="avatar-crop-title" className="text-base font-semibold text-gray-900">
            Position your photo
          </h2>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loadFailed ? (
            <p role="alert" className="text-sm text-red-600">
              That image could not be prepared. Please try a different file.
            </p>
          ) : (
            <>
              {/* Editing viewport. The circular mask shows exactly what the
                  avatar will look like; the square is what gets exported. */}
              <div
                role="application"
                aria-label="Drag to reposition, arrow keys to nudge"
                tabIndex={0}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                onKeyDown={handleViewportKeyDown}
                style={{ width: VIEWPORT_SIZE_PIXELS, height: VIEWPORT_SIZE_PIXELS }}
                className="relative mx-auto cursor-move touch-none overflow-hidden rounded-full bg-gray-100 ring-1 ring-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {objectUrl && (
                  <img
                    src={objectUrl}
                    alt=""
                    draggable={false}
                    onLoad={handleImageLoad}
                    onError={() => setLoadFailed(true)}
                    style={{
                      width: renderedWidth || undefined,
                      height: renderedHeight || undefined,
                      transform: `translate(calc(-50% + ${panOffset.x}px), calc(-50% + ${panOffset.y}px))`,
                    }}
                    className="pointer-events-none absolute left-1/2 top-1/2 max-w-none"
                  />
                )}
                {!isReady && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                  </div>
                )}
              </div>

              <div className="mt-5 flex items-center gap-3">
                <ZoomIn className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                <input
                  type="range"
                  min={MIN_ZOOM}
                  max={MAX_ZOOM}
                  step={ZOOM_STEP}
                  value={zoom}
                  aria-label="Zoom"
                  disabled={!isReady}
                  onChange={(changeEvent) => setZoom(Number(changeEvent.target.value))}
                  className="h-1 w-full cursor-pointer appearance-none rounded-full bg-gray-200 accent-blue-600"
                />
              </div>

              <p className="mt-3 text-xs text-gray-400">
                Drag the photo to reposition it, or use the arrow keys.
              </p>
            </>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 px-6 py-4 border-t border-gray-100">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isExporting}>
            Cancel
          </Button>
          <Button type="button" onClick={handleApply} disabled={!isReady || isExporting}>
            {isExporting ? "Preparing…" : "Use photo"}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
